const AUTH_SESSION_ID_KEY = "mayaAuthSessionId";
const PROFILE_BOOTSTRAP_SESSION_KEY = "mayaProfileBootstrapSessionId";
const ORG_SWITCH_NAVIGATION_KEY = "mayaOrgSwitchNavigation";
const LEGACY_DASHBOARD_PROFILE_BOOTSTRAP_KEY =
  "dashboardInitialProfileFetchComplete";
let hasProcessedDashboardBootstrapForRuntime = false;

const safeSessionStorageRead = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSessionStorageWrite = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Ignore storage access issues.
  }
};

const safeSessionStorageRemove = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore storage access issues.
  }
};

export const clearLegacyDashboardBootstrapFlag = () => {
  try {
    sessionStorage.removeItem(LEGACY_DASHBOARD_PROFILE_BOOTSTRAP_KEY);
  } catch {
    // Ignore storage access issues.
  }
};

const getCryptoApi = () => {
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    return window.crypto;
  }

  throw new Error("Secure random number generator is unavailable.");
};

const createSecureRandomHex = (byteLength) => {
  const randomBytes = new Uint8Array(byteLength);
  getCryptoApi().getRandomValues(randomBytes);

  return Array.from(randomBytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
};

const createAuthSessionId = () =>
  `auth-${Date.now()}-${createSecureRandomHex(16)}`;

export const beginNewAuthSession = () => {
  const sessionId = createAuthSessionId();

  safeSessionStorageWrite(AUTH_SESSION_ID_KEY, sessionId);
  safeSessionStorageRemove(PROFILE_BOOTSTRAP_SESSION_KEY);
  clearLegacyDashboardBootstrapFlag();

  return sessionId;
};

export const getActiveAuthSessionId = () =>
  safeSessionStorageRead(AUTH_SESSION_ID_KEY);

export const hasCompletedProfileBootstrap = () => {
  const activeSessionId = getActiveAuthSessionId();
  const bootstrappedSessionId = safeSessionStorageRead(
    PROFILE_BOOTSTRAP_SESSION_KEY,
  );

  return Boolean(
    activeSessionId &&
    bootstrappedSessionId &&
    activeSessionId === bootstrappedSessionId,
  );
};

export const shouldBootstrapDashboardEntry = () => {
  const shouldBootstrap = !hasCompletedProfileBootstrap();
  hasProcessedDashboardBootstrapForRuntime = true;
  return shouldBootstrap;
};

export const skipDashboardBootstrapForRuntime = () => {
  hasProcessedDashboardBootstrapForRuntime = true;
};

export const markProfileBootstrapComplete = () => {
  const activeSessionId = getActiveAuthSessionId() || beginNewAuthSession();
  safeSessionStorageWrite(PROFILE_BOOTSTRAP_SESSION_KEY, activeSessionId);
  try {
    sessionStorage.removeItem(ORG_SWITCH_NAVIGATION_KEY);
  } catch {
    // Ignore storage access issues.
  }
  clearLegacyDashboardBootstrapFlag();
  return activeSessionId;
};

export const markOrgSwitchNavigation = () => {
  try {
    sessionStorage.setItem(ORG_SWITCH_NAVIGATION_KEY, "true");
  } catch {
    // Ignore storage access issues.
  }
};

export const hasPendingOrgSwitchNavigation = () => {
  try {
    return sessionStorage.getItem(ORG_SWITCH_NAVIGATION_KEY) === "true";
  } catch {
    return false;
  }
};

export const consumeOrgSwitchNavigation = () => {
  try {
    const shouldSkipBootstrap =
      sessionStorage.getItem(ORG_SWITCH_NAVIGATION_KEY) === "true";

    sessionStorage.removeItem(ORG_SWITCH_NAVIGATION_KEY);

    return shouldSkipBootstrap;
  } catch {
    return false;
  }
};

export const resetAuthSessionState = () => {
  hasProcessedDashboardBootstrapForRuntime = false;
  safeSessionStorageRemove(AUTH_SESSION_ID_KEY);
  safeSessionStorageRemove(PROFILE_BOOTSTRAP_SESSION_KEY);
  try {
    sessionStorage.removeItem(ORG_SWITCH_NAVIGATION_KEY);
  } catch {
    // Ignore storage access issues.
  }
  clearLegacyDashboardBootstrapFlag();
};
